import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { MAX_REQUESTS, PAGE_LIMIT } from "../api/paging";
import { useScanDetail, useScanOffersByMode, useScans } from "../api/queries";
import type { OfferMode } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { Variation } from "../components/Variation";
import { adena, signed, when } from "../format";
import { itemHref, traderHref } from "../search/nav";
import type { AggSort } from "./offersSort";
import {
  aggregateOffers, filterAggregates, sortAggregates, sortOffers,
} from "./offersSort";
import { scanWalked } from "./scanRoute";

const SORTS: AggSort[] = ["item", "best", "traders"];

/** Craft is the same row shape with different words over it: a crafter charging
 *  a fee is still one trader offering one thing at one price. Migration 003 is
 *  clear that the fee is not an ask, which is why it gets no mode colour — rose
 *  and olive are WTS and WTB, and a fee is neither. */
const LABELS: Record<OfferMode, {
  title: string; who: string; counter: string; best: string; price: string; hue: string;
}> = {
  buy: { title: "Buy", who: "Trader", counter: "Buyers", best: "Best buy", price: "Price", hue: "buy" },
  sell: { title: "Sell", who: "Trader", counter: "Sellers", best: "Best sell", price: "Price", hue: "sell" },
  craft: { title: "Craft", who: "Crafter", counter: "Crafters", best: "Best fee", price: "Fee", hue: "" },
};

export function Offers({ mode }: { mode: OfferMode }) {
  const { uid = "" } = useParams();
  const [sort, setSort] = useState<AggSort>("item");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<number | null>(null);

  const scans = useScans();
  const batch = useScanOffersByMode(uid, mode);
  // Only for the "vs last" column, and only where one exists: best_prices is
  // restricted to buy and sell server-side, so craft has no previous-scan
  // figure to show and does not ask for one. react-query dedupes this with the
  // index's own call, so on the common path it costs no request.
  const detail = useScanDetail(mode === "craft" ? "" : uid, "item");
  const label = LABELS[mode];
  const scan = scans.data?.find((s) => s.scan_uid === uid);

  if (batch.isError) {
    return (
      <>
        <TitleBand title={label.title} />
        <ErrorBand
          message={`Could not load this scan's ${mode} offers.`}
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (!batch.data) {
    return <><TitleBand title={label.title} /><Skeleton rows={8} /></>;
  }

  const aggregates = sortAggregates(
    filterAggregates(aggregateOffers(batch.data.rows, mode), filter), sort, mode);
  const needle = filter.trim();
  const totalOffers = aggregates.reduce((sum, agg) => sum + agg.offers, 0);

  // Keyed by item so a row can show its move against the previous scan from the
  // same machine and city, exactly as the index's table does.
  const deltas = new Map(
    (detail.data?.best ?? []).map((row) => [
      row.item_id, mode === "buy" ? row.delta_buy : row.delta_sell,
    ]),
  );

  // The selected item's own offers, best first — the detail the table used to
  // show on every row, now one item at a time.
  const chosen = aggregates.find((agg) => agg.item_id === selected);
  const chosenOffers = selected === null
    ? []
    : sortOffers(batch.data.rows.filter((row) => row.item_id === selected), "price", mode);

  return (
    <>
      <TitleBand
        title={label.title}
        note={`${aggregates.length} items, ${totalOffers} offers`}
      />
      {/* The town and the moment, once. Both are facts about the scan rather
          than about a row, so a column would repeat one value down the whole
          page; without them the page never says which town it is showing. A
          scan older than the 50 the list holds has no summary to read. */}
      {scan ? (
        <p className="scan-meta">
          <span>{scan.city || "unknown town"}</span>
          <span aria-hidden="true">·</span>
          <span>{when(scan.started_at)}</span>
        </p>
      ) : null}
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {option}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {/* A scan that never walked a mode is not a market with nothing in it,
            and one message for both would have someone hunting for a market
            problem that is a scan setting. */}
        {aggregates.length === 0 ? (
          needle ? (
            <Empty>Nothing matching “{needle}”.</Empty>
          ) : scanWalked(scan, mode) ? (
            <Empty>No {mode} offers in this scan.</Empty>
          ) : (
            <Empty>
              This scan didn’t walk {mode}. Run a scan with {mode} enabled to see
              them here.
            </Empty>
          )
        ) : (
          <>
            {batch.data.truncated ? (
              // Not "the first N": the server orders observed_at DESC, so the
              // cap keeps the most recently recorded rows, not the earliest.
              // "may hold more" is deliberate: next_cursor is set whenever a
              // page comes back full, so a scan sitting exactly at the cap
              // looks identical to one that overflowed it, and the copy must
              // not claim to tell those apart.
              <p className="scan-meta">
                built from the {(MAX_REQUESTS * PAGE_LIMIT).toLocaleString()} most
                recently recorded offers — this scan may hold more, so the best
                price here may not be the scan’s best
              </p>
            ) : null}
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">{label.best}</th>
                    <th className="num">{label.counter}</th>
                    {mode === "craft" ? null : <th className="num">vs last</th>}
                  </tr>
                </thead>
                <tbody>
                  {aggregates.map((agg) => (
                    <tr
                      key={agg.item_id}
                      className={agg.item_id === selected ? "sel" : undefined}
                      onClick={() =>
                        setSelected(agg.item_id === selected ? null : agg.item_id)}
                    >
                      <td>
                        <Link
                          to={itemHref(agg.item_id, agg.item)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {agg.item}
                        </Link>
                      </td>
                      <td className={`num ${label.hue}`}>{adena(agg.best)}</td>
                      <td className="num dim">{agg.traders}</td>
                      {mode === "craft" ? null : (
                        <td
                          className={`num ${deltas.get(agg.item_id) == null ? "dim" : ""}`}
                          title="Against the previous scan from the same machine and city"
                        >
                          {signed(deltas.get(agg.item_id) ?? null)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {chosen ? (
        <>
          <TitleBand
            title={chosen.item}
            note={`${chosenOffers.length} in this scan, best first`}
          />
          <Panel>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>{label.who}</th>
                    {mode === "craft" ? null : <th className="num">Qty</th>}
                    <th className="num">{label.price}</th>
                  </tr>
                </thead>
                <tbody>
                  {chosenOffers.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={traderHref(row.nick)}>{row.nick}</Link>
                        {/* Here rather than in the item table above it: that
                            table is one row per item, and two traders' daggers
                            differ by exactly this. */}
                        <Variation {...row} />
                      </td>
                      {mode === "craft" ? null : <td className="num">{adena(row.qty)}</td>}
                      <td className={`num ${label.hue}`}>{adena(row.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      ) : null}
    </>
  );
}
